import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { SessionUser, Permission } from "@sbd-tcic/shared-types";
import { api, ApiClientError } from "./api";

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<SessionUser>("/auth/me")
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  async function login(email: string, password: string) {
    const sessionUser = await api.post<SessionUser>("/auth/login", { email, password });
    setUser(sessionUser);
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch {
      // Ignore — we clear local state regardless (ADR-013 note: fail-safe logout).
    }
    setUser(null);
  }

  function can(permission: Permission) {
    return user?.permissions.includes(permission) ?? false;
  }

  return <AuthContext.Provider value={{ user, loading, login, logout, can }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider.");
  return ctx;
}

export { ApiClientError };
